import type { UpdateDepartmentTypeMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

const mapTrpcResponseToGraphQL = (): UpdateDepartmentTypeMutation => {
  return {
    update_department_type: {
      __typename: 'department_type_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateDepartmentTypeTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.department.updateType.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.department.allTypes.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.department.departmentTypeById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.department.departmentTypeGroups.queryKey(),
        });
      },
    }),
  });

  return {
    updateDepartmentType: async (variables: {
      DepartmentTypeId: string;
      Name?: string | null;
      Description?: string | null;
      DepartmentTypeGroupId?: string | null;
      // Required for optimistic locking — the repository WHERE clause matches
      // against the previous ModifiedAtTimestamp. Must be the timestamp read
      // back from the record being edited, never a freshly generated one.
      OriginalTimestamp: string;
    }): Promise<UpdateDepartmentTypeMutation> => {
      await mutation.mutateAsync({
        Id: variables.DepartmentTypeId,
        Name: variables.Name,
        Description: variables.Description,
        DepartmentTypeGroupId: variables.DepartmentTypeGroupId,
        OriginalTimestamp: variables.OriginalTimestamp,
      });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
    data: mutation.data ? mapTrpcResponseToGraphQL() : undefined,
  };
};
